import { useEffect, useState } from "react";
import { TextField, Box, Typography } from "@mui/material";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { SoftButton } from "../../hatim/soft/SoftKit";
import { useUpdateHatimRepeatMutation } from "../../../api/hatimManagementApi";
import { useToast } from "../../../components/ui/Toast";
import { format } from "../../../translations";

/**
 * Tekrar sayısı düzenleme. `minValue` = şu anki dönem numarası: geçmiş
 * dönemler geri alınamayacağı için daha küçük değer kabul edilmez.
 */
export default function RepeatDurationInline({ hatimTemplateId, currentValue, minValue }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(currentValue ?? ""));
  const [updateRepeat, { isLoading }] = useUpdateHatimRepeatMutation();

  useEffect(() => {
    setDraft(String(currentValue ?? ""));
  }, [currentValue]);

  const num = Number(draft);
  const min = Math.max(1, Number(minValue) || 1);
  const belowMin = String(draft).trim() !== "" && Number.isFinite(num) && num < min;
  const canSave =
    String(draft).trim() !== "" &&
    Number.isFinite(num) &&
    num >= min &&
    num !== Number(currentValue) &&
    !isLoading;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      await updateRepeat({ hatimTemplateId, repeatDuration: num }).unwrap();
      toast.success(t("hatim_mgmt_repeat_updated"));
      setEditing(false);
    } catch (err) {
      const msg = err?.message || err?.data?.message || t("hatim_mgmt_action_error");
      toast.error(msg);
    }
  };

  if (!editing) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" color="text.secondary">
          {t("hatim_mgmt_repeat_label")}: <strong>{currentValue ?? "—"}</strong>
        </Typography>
        <SoftButton tone="soft" size="small" onClick={() => setEditing(true)}>
          {t("hatim_mgmt_repeat_edit_cta")}
        </SoftButton>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="flex-start" gap={1}>
      <TextField
        type="number"
        size="small"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        inputProps={{ min, step: 1 }}
        sx={{ width: 150 }}
        autoFocus
        error={belowMin}
        helperText={
          belowMin
            ? format(t("hatim_mgmt_repeat_below_min"), { n: min })
            : format(t("hatim_mgmt_repeat_min_hint"), { n: min })
        }
      />
      <SoftButton tone="success" size="small" onClick={handleSave} disabled={!canSave}>
        {t("hatim_mgmt_repeat_save")}
      </SoftButton>
      <SoftButton
        tone="cancel"
        size="small"
        onClick={() => {
          setDraft(String(currentValue ?? ""));
          setEditing(false);
        }}
        disabled={isLoading}
      >
        {t("hatim_mgmt_repeat_cancel")}
      </SoftButton>
    </Box>
  );
}
